/**
 * What travels with the guest when the checkout hands them back to a partner.
 *
 * A partner journey ends on the partner's own domain, so their GA4 sees a fresh
 * browser: a new user, a new session and the booking attributed to "direct".
 * Forwarding the identifiers GA4 already holds for the guest, together with the
 * campaign that brought them to us, is what keeps one session and one
 * attribution across the hop.
 *
 * Everything forwarded is allow-listed by name, bounded in length and validated
 * against the shape it is supposed to have. Nothing guest-supplied is passed
 * through unvalidated, and nothing describing the guest is forwarded at all: no
 * name, email, telephone number or booking detail appears here.
 */

/** Parameter carrying GA4's client identifier for our own property. */
export const CLIENT_ID_PARAM = "ga_client_id";

/** Parameter carrying GA4's session identifier for our own property. */
export const SESSION_ID_PARAM = "ga_session_id";

/**
 * Campaign parameters forwarded when they arrived on our own URL.
 *
 * The standard `utm_*` set plus `gclid`, which is what a partner's GA4 needs to
 * attribute the booking to the campaign that produced it rather than to us.
 */
export const CAMPAIGN_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
] as const;

/** Longest campaign value forwarded. Google's own limit is 100 bytes. */
const MAX_CAMPAIGN_VALUE_LENGTH = 128;

/**
 * Characters a forwarded campaign value may contain.
 *
 * A single bounded character class, so the match is linear: no grouping and no
 * alternation, and therefore nothing to backtrack over. Anything outside it —
 * whitespace, control characters, markup, a nested query string — means the
 * value is not a campaign tag and is dropped rather than sanitised.
 */
const CAMPAIGN_VALUE_PATTERN = /^[A-Za-z0-9._~%+-]{1,128}$/;

/** GA4 client identifiers are two dot-separated integers, for example `12345.67890`. */
const CLIENT_ID_PATTERN = /^\d{1,20}\.\d{1,20}$/;

/** GA4 session identifiers are an epoch-second integer. */
const SESSION_ID_PATTERN = /^\d{1,20}$/;

/** A validated client identifier, or `null`. */
export function validateClientId(value: string | null): string | null {
  return value && CLIENT_ID_PATTERN.test(value) ? value : null;
}

/** A validated session identifier, or `null`. */
export function validateSessionId(value: string | null): string | null {
  return value && SESSION_ID_PATTERN.test(value) ? value : null;
}

/**
 * The campaign parameters that arrived on our own URL, validated.
 *
 * @param search query string to read; defaults to the current page's.
 */
export function readCampaignParams(search: string = window.location.search): Record<string, string> {
  const params = new URLSearchParams(search);
  const forwarded: Record<string, string> = {};

  for (const name of CAMPAIGN_PARAMS) {
    const value = params.get(name);
    if (!value || value.length > MAX_CAMPAIGN_VALUE_LENGTH) continue;
    if (!CAMPAIGN_VALUE_PATTERN.test(value)) continue;

    forwarded[name] = value;
  }

  return forwarded;
}

/**
 * Appends continuity parameters to a partner's return URL.
 *
 * An existing parameter of the same name is overwritten, so a partner's own URL
 * cannot end up carrying two client identifiers.
 *
 * @returns the URL to navigate to, or the original string when it is not a URL
 * we can parse — the redirect still happens, simply without continuity.
 */
export function appendContinuityParams(returnUrl: string, continuity: Record<string, string>): string {
  try {
    const url = new URL(returnUrl);
    for (const [name, value] of Object.entries(continuity)) {
      url.searchParams.set(name, value);
    }

    return url.toString();
  } catch {
    return returnUrl;
  }
}
